// Earned-value view (wireframe 07) — 6 KPI cards + planned/earned/actual
// chart + variance-by-phase table. Right pin pane holds Forecast (EAC,
// VAC, TCPI, ETC) and Largest variance.

import { useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from "recharts";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

interface CurvePoint { date: string; value: number; }

interface PhaseRow {
  phase_id: string;
  code: string | null;
  name: string;
  start: string | null;
  end: string | null;
  budget: number;
  planned: number;
  earned: number;
  actual: number | null;
  cpi: number | null;
  spi: number | null;
  eac: number | null;
}

interface EvmPayload {
  bac: number;
  currency: string;
  data_date: string;
  project_start: string | null;
  project_end: string | null;
  kpis: {
    bac: number;
    planned_to_date: number;
    earned_to_date: number;
    actual_to_date: number | null;
    cpi: number | null;
    spi: number | null;
  };
  forecast: {
    eac_cpi: number | null;
    vac: number | null;
    tcpi: number | null;
    etc: number | null;
  };
  curves: {
    planned: CurvePoint[];
    earned: CurvePoint[];
    actual: CurvePoint[] | null;
  };
  phase_variance: PhaseRow[];
  largest_variance: Array<{ phase: string; delta: number }>;
}

export default function EarnedValueView() {
  const { project } = useShell();
  const { env, loading, error, refetch } = useTool("render_earned_value", { project });
  const data = (env?.data ?? {}) as Partial<EvmPayload>;
  const currency = data.currency ?? "USD";

  useRightPane(
    useMemo(() => <RightPane data={data} currency={currency} />, [data, currency]),
  );

  if (loading) return <Skeleton lines={6} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className="space-y-3">
      <KpiRow data={data} currency={currency} />
      <ChartCard data={data} currency={currency} />
      <VarianceTable rows={data.phase_variance ?? []} currency={currency} />
    </div>
  );
}

function KpiRow({ data, currency }: { data: Partial<EvmPayload>; currency: string }) {
  const k = data.kpis;
  return (
    <section className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
      <KpiCard label="Budget at completion" value={fmtMoney(data.bac ?? 0, currency)} />
      <KpiCard label="Planned to date" value={fmtMoney(k?.planned_to_date ?? 0, currency)} />
      <KpiCard label="Earned to date"  value={fmtMoney(k?.earned_to_date ?? 0, currency)} />
      <KpiCard label="Actual to date"
               value={k?.actual_to_date == null ? "—" : fmtMoney(k.actual_to_date, currency)}
               sub={k?.actual_to_date == null ? "AC feed not wired" : undefined}
               subTone="muted" />
      <KpiCard label="Cost performance (CPI)"
               value={k?.cpi == null ? "—" : k.cpi.toFixed(2)}
               sub={k?.cpi == null ? "needs AC" : (k.cpi >= 1 ? "on or above 1.0" : "below 1.0")}
               subTone={k?.cpi != null && k.cpi < 1 ? "rose" : "muted"} />
      <KpiCard label="Schedule performance (SPI)"
               value={k?.spi == null ? "—" : k.spi.toFixed(2)}
               sub={k?.spi == null ? "no schedule data"
                                   : (k.spi >= 1 ? "on or above 1.0" : "behind")}
               subTone={k?.spi != null && k.spi < 1 ? "amber" : "muted"} />
    </section>
  );
}

function KpiCard({ label, value, sub, subTone }: {
  label: string;
  value: string;
  sub?: string;
  subTone?: "muted" | "amber" | "rose";
}) {
  const subClass =
    subTone === "rose"  ? "text-rose-600" :
    subTone === "amber" ? "text-amber-600" :
                          "text-slate-500";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
      {sub && <div className={`text-xs ${subClass}`}>{sub}</div>}
    </div>
  );
}

function ChartCard({ data, currency }: { data: Partial<EvmPayload>; currency: string }) {
  const points = useMemo(() => {
    const planned = data.curves?.planned ?? [];
    const earned = data.curves?.earned ?? [];
    const earnedByDate = new Map(earned.map((p) => [p.date, p.value]));
    return planned.map((p) => ({
      date: p.date,
      planned: p.value,
      earned: earnedByDate.get(p.date) ?? null,
    }));
  }, [data.curves]);

  const today = data.data_date ?? null;
  const noData = points.length === 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-700">Planned vs earned vs actual</div>
        {data.kpis?.actual_to_date == null && (
          <div className="text-[11px] text-slate-500">
            actual (ACWP) feed not wired — chart shows PV + EV only
          </div>
        )}
      </div>
      {noData ? (
        <div className="p-4 text-sm text-slate-500">
          No EVM curve data. Check that <code>budget_versions</code> and
          <code> schedule.csv</code> exist.
        </div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)}
                     stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${currency === "USD" ? "$" : ""}${Math.round(v / 1000)}k`}
                     stroke="#64748b" tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: any) => v == null ? "—" : fmtMoney(Number(v), currency)}
                labelStyle={{ fontSize: 11 }}
                contentStyle={{ fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {today && (
                <ReferenceLine x={today} stroke="#f59e0b" strokeDasharray="3 3"
                               label={{ value: "today", fontSize: 10, fill: "#b45309" }} />
              )}
              <Line type="monotone" dataKey="planned" name="planned (BCWS)"
                    stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="earned" name="earned (BCWP)"
                    stroke="#0ea5e9" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function VarianceTable({ rows, currency }: { rows: PhaseRow[]; currency: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">Variance by phase</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500">No phase budget rows in canonical.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="text-left font-normal">phase</th>
              <th className="text-right font-normal">budget</th>
              <th className="text-right font-normal">planned</th>
              <th className="text-right font-normal">earned</th>
              <th className="text-right font-normal">actual</th>
              <th className="text-right font-normal">CPI</th>
              <th className="text-right font-normal">SPI</th>
              <th className="text-right font-normal">EAC</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.phase_id} className="border-b border-slate-50 last:border-b-0">
                <td className="py-1.5 pr-2 text-slate-800">
                  {r.code ? `${r.code} ` : ""}{r.name}
                </td>
                <td className="py-1.5 text-right font-mono">{fmtMoney(r.budget, currency)}</td>
                <td className="py-1.5 text-right font-mono">{fmtMoney(r.planned, currency)}</td>
                <td className="py-1.5 text-right font-mono">{fmtMoney(r.earned, currency)}</td>
                <td className="py-1.5 text-right font-mono">
                  {r.actual == null ? "—" : fmtMoney(r.actual, currency)}
                </td>
                <td className="py-1.5 text-right">{r.cpi == null ? "—" : r.cpi.toFixed(2)}</td>
                <td className="py-1.5 text-right">{r.spi == null ? "—" : r.spi.toFixed(2)}</td>
                <td className="py-1.5 text-right">{r.eac == null ? "—" : fmtMoney(r.eac, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RightPane({ data, currency }: { data: Partial<EvmPayload>; currency: string }) {
  const f = data.forecast;
  const lv = data.largest_variance ?? [];
  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Forecast</div>
        <div className="mt-2 rounded border border-slate-200 p-2 text-xs">
          <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-y-1">
            <dt className="text-slate-500">EAC (CPI method)</dt>
            <dd>{f?.eac_cpi == null ? "—" : fmtMoney(f.eac_cpi, currency)}</dd>
            <dt className="text-slate-500">VAC</dt>
            <dd className={f?.vac != null && f.vac < 0 ? "text-rose-600" : ""}>
              {f?.vac == null ? "—" : fmtMoney(f.vac, currency)}
            </dd>
            <dt className="text-slate-500">TCPI</dt>
            <dd>{f?.tcpi == null ? "—" : f.tcpi.toFixed(2)}</dd>
            <dt className="text-slate-500">ETC</dt>
            <dd>{f?.etc == null ? "—" : fmtMoney(f.etc, currency)}</dd>
          </dl>
          <div className="mt-2 text-[10px] text-slate-400">
            All four require an Actual-cost feed which is not yet wired.
          </div>
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Largest variance</div>
        <ul className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs">
          {lv.length === 0 ? (
            <li className="text-slate-500">No variance — earned ≈ planned for every phase.</li>
          ) : lv.map((v) => (
            <li key={v.phase} className="flex items-center justify-between">
              <span className="truncate pr-2 text-slate-700">{v.phase}</span>
              <span className={"font-mono " + (v.delta < 0 ? "text-rose-600" : "text-emerald-600")}>
                {v.delta >= 0 ? "+" : "−"}{fmtMoney(Math.abs(v.delta), currency)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function fmtMoney(v: number, currency: string): string {
  const sign = v < 0 ? "−" : "";
  const abs = Math.abs(v);
  if (currency === "USD") {
    return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `${sign}${abs.toLocaleString()} ${currency}`;
}
